import {
  alpha,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import CopyText from 'src/components/common/CopyText';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { useMutateCreateIntentBankTransfer } from 'src/services/mutate';
import { renderNumberOrDefault, renderPriceOrDefault } from 'src/utils/helpers/stringHelpers';
import { ICreateIntentTransfer } from 'src/utils/typings/response/payment';
import ButtonBack from 'src/components/common/ButtonBack';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { useNavigate } from 'react-router-dom';
import FormWarningQuit from 'src/features/BankTransfer/FormWarningQuit';
import { QUERY_KEY_USAGE, useQueryInvoiceDetail } from 'src/services/queries';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import useSnackbarStore from 'src/zustand/useSnackbarStore';
import useRefetch from 'src/hooks/useRefetch';
import { SUPPORT_EMAIL } from 'src/utils/constants/config';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
}));

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius * 2,
}));

const BankTransfer = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dispatch } = useSnackbarStore();
  const { refetchQuery } = useRefetch();
  const { priceId, planId, customerId, campaignSlug, type } = useQueryParams();
  const { mutateAsync: mutateAsyncCreateIntent, isPending } = useMutateCreateIntentBankTransfer();
  const [intentPayment, setIntentPayment] = useState<ICreateIntentTransfer | null>(null);
  const [openFormQuit, setOpenFormQuit] = useState(false);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [startPolling, setStartPolling] = useState(true);
  const { data } = useQueryInvoiceDetail(invoiceId || '', startPolling);

  const createIntentPayment = async () => {
    const windowVar: ExpectedAny = window;
    const response = await mutateAsyncCreateIntent({
      customer_id: customerId || '',
      price_id: priceId,
      plan_id: planId,
      client_reference_id: windowVar?.affiliateId || '',
    });
    setIntentPayment(response?.data);
    setInvoiceId(response?.data?.payment_intent_id);
  };

  useEffect(() => {
    createIntentPayment();
  }, []);

  useEffect(() => {
    if (data?.status_code === SUCCESS_CODE.GET) {
      dispatch({
        title: 'Payment Successful',
        description: `Thank you! We have received payment amount of $${renderNumberOrDefault(intentPayment?.amount)}`,
        open: true,
        color: 'success',
        severity: 'success',
      });
      setStartPolling(false);
      refetchQuery([QUERY_KEY_USAGE]);

      const timeout = setTimeout(() => {
        const params = handleQueryParamFromObject({
          invoice_id: invoiceId,
        });
        navigate({ pathname: AuthRouteKeys.OrderConfirmation, search: params });
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [JSON.stringify(data)]);

  const backToCheckout = () => {
    const params = handleQueryParamFromObject({
      planId,
      campaignSlug,
      type,
    });
    navigate({ pathname: AuthRouteKeys.Checkout, search: params });
  };

  return (
    <StyledContainer maxWidth="xl">
      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={isPending}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <FormWarningQuit
        open={openFormQuit}
        handleClose={() => setOpenFormQuit(false)}
        handleConfirm={() => backToCheckout()}
      />
      <ButtonBack
        onClick={() => {
          setOpenFormQuit(true);
        }}
      />
      <Box display={'flex'} flexDirection={'column'} gap={3} justifyContent={'center'} alignItems={'center'}>
        <Typography variant="h5" align="left">
          {t('bank_transfer.title')}
        </Typography>
        <Typography variant="h5" color="primary.main">
          Amount to Pay: ${renderPriceOrDefault(intentPayment?.amount)}
        </Typography>
        <StyledBox width="600px" bgcolor={theme.palette.background.default} gap={1}>
          <Typography variant="h6">Review Your Payment</Typography>
          <Typography variant="body1" color="text.primary">
            Please transfer the exact amount to the following bank account using the information below.
          </Typography>
          <Typography variant="body1" color="text.primary">
            <Typography component="span" variant="body1" color="warning.main" fontWeight="bold">
              Important:{' '}
            </Typography>
            Include the <b>Reference Code</b> exactly as shown, so we can match your payment automatically.
          </Typography>
        </StyledBox>
        <StyledBox width="600px" bgcolor={theme.palette.background.default}>
          <Typography variant="h6" mb={2.5}>
            Bank Information
          </Typography>
          <StyledBox bgcolor={alpha(theme.palette.divider, 0.2)} border={`1px solid ${theme.palette.divider}`} mb={2}>
            <Stack direction="column" spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="text.secondary">
                  Bank name
                </Typography>
                <Typography variant="body1" align="right" fontWeight="bold">
                  {intentPayment?.funding_instructions.bank_name}
                  <CopyText text={intentPayment?.funding_instructions.bank_name} />
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="text.secondary">
                  Account number
                </Typography>
                <Typography variant="body1" align="right" fontWeight="bold">
                  {intentPayment?.funding_instructions.account_number}
                  <CopyText text={intentPayment?.funding_instructions.account_number} />
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="text.secondary">
                  SWIFT Code (BIC)
                </Typography>
                <Typography variant="body1" align="right" fontWeight="bold">
                  {intentPayment?.funding_instructions.swift_code}
                  <CopyText text={intentPayment?.funding_instructions.swift_code} />
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="text.secondary">
                  Routing number
                </Typography>
                <Typography variant="body1" align="right" fontWeight="bold">
                  {intentPayment?.funding_instructions.routing_number}
                  <CopyText text={intentPayment?.funding_instructions.routing_number} />
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body1" color="text.secondary">
                  Reference
                </Typography>
                <Typography variant="body1" align="right" color="secondary.main" fontWeight="bold">
                  {intentPayment?.reference}
                  <CopyText text={intentPayment?.reference} muiProps={{ sx: { color: 'secondary.main' } }} />
                </Typography>
              </Stack>
            </Stack>
          </StyledBox>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              backToCheckout();
            }}
          >
            Change Different Payment Method
          </Button>
        </StyledBox>
        <StyledBox width="600px" bgcolor={theme.palette.background.default} gap={1}>
          <Typography variant="h6">Need Help?</Typography>
          <Typography variant="body1" color="text.secondary">
            If you encounter any issues or transferred the wrong amount, please contact our support team:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Our team will help verify and allocate your payment correctly.
          </Typography>
        </StyledBox>
      </Box>
    </StyledContainer>
  );
};

export default BankTransfer;
